import { Link } from "react-router-dom";
import { CONTACT_ADDRESS, CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_FORMATTED } from "utils/contactInfo";
import { Instagram, Linkedin, Mail, Phone, MapPin } from "lucide-react";
import { asset } from "../../lib/assets";

const logoAsset = { url: asset("violerts-logo-02.png") };
const vendorAcris = { url: asset("vendor-acris.png") };
const vendorDobNow = { url: asset("vendor-dob-now.png") };
const vendorHpd = { url: asset("vendor-hpd.png") };
const vendorEcb = { url: asset("vendor-ecb.png") };
const vendorPortal = { url: asset("vendor-portal.png") };
const vendors = [
    { name: "Azark", src: vendorAcris.url, href: "https://azark.com", size: "h-4 w-4" },
    { name: "BVS", src: vendorDobNow.url, href: "https://bvs.nyc", size: "h-4 w-4" },
    { name: "AAPC", src: vendorHpd.url, href: "https://aapc.nyc", size: "h-5 w-5" },
    { name: "Arkco", src: vendorEcb.url, href: "https://arkco.nyc", size: "h-5 w-5" },
    { name: "VPS", src: vendorPortal.url, href: "https://vps.nyc", size: "h-5 w-5" },
];
const contactDetails = [
    // Same targets as the previous footer: mailto / tel / Google Maps.
    { icon: Mail, label: "Email", value: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
    { icon: Phone, label: "Phone", value: CONTACT_PHONE_FORMATTED, href: `tel:${CONTACT_PHONE}` },
    { icon: MapPin, label: "Office", value: CONTACT_ADDRESS, href: "https://maps.app.goo.gl/3S2kgXWk48vREADu9", external: true },
];
const legalLinks = [
    { to: "/use-policy", label: "Acceptable Use Policy" },
    { to: "/sms-terms", label: "SMS Terms" },
    { to: "/disclaimer", label: "Disclaimer" },
];
const resourceLinks = [
    { to: "/backend-search", label: "Backend search" },
    { href: "https://ecb.vio.nyc/", label: "ECB Code Search" },
    { to: "/glossary", label: "Glossary" },
    // Blog is not ported yet (VWA-2898 scope); add { to: "/blog", label: "Blog" } back with the blog pages.
    { to: "/faqs", label: "FAQs" },
];
export function SiteFooter() {
    return (<footer className="surface-ink">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 pt-10 pb-6 md:grid-cols-[1.4fr_1fr_1fr]">
        <div>
          <img src={logoAsset.url} alt="Violerts" className="h-10 w-auto"/>
          <p className="mt-4 max-w-xs text-sm opacity-70">
            Property compliance monitoring, violation resolution and agency data — in one
            place.
          </p>
          <Link to="/sign-up" className="mt-5 inline-flex text-sm font-semibold text-signal underline-offset-4 hover:underline">
            Start monitoring your portfolio →
          </Link>
        </div>

        <div>
          <h3 className="eyebrow opacity-60">Contact</h3>
          <ul className="mt-4 space-y-3">
            {contactDetails.map((d) => (<li key={d.label}>
                <a href={d.href} {...(d.external ? { target: "_blank", rel: "noreferrer" } : {})} className="flex items-center gap-3 text-sm opacity-80 hover:opacity-100">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink-foreground/10 text-ink-foreground">
                    <d.icon className="h-3.5 w-3.5"/>
                  </span>
                  <span className="block font-medium">{d.value}</span>
                </a>
              </li>))}
          </ul>
        </div>

        <div>
          <h3 className="eyebrow opacity-60">Resources</h3>
          <ul className="mt-4 space-y-2.5">
            {resourceLinks.map((l) => (<li key={l.label}>
                {"href" in l ? (<a href={l.href} target="_blank" rel="noreferrer" className="block text-sm leading-snug opacity-80 hover:opacity-100">
                    {l.label}
                  </a>) : (<Link to={l.to} className="block text-sm leading-snug opacity-80 hover:opacity-100">
                    {l.label}
                  </Link>)}
              </li>))}
          </ul>
        </div>
      </div>

      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-5 pb-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <a href="https://www.instagram.com/violerts.nyc/" target="_blank" rel="noreferrer" aria-label="Instagram" className="flex h-9 w-9 items-center justify-center rounded-full bg-ink-foreground/10 text-ink-foreground transition-colors hover:bg-signal hover:text-signal-foreground">
            <Instagram className="h-4 w-4"/>
          </a>
          <a href="https://www.linkedin.com/company/violerts/" target="_blank" rel="noreferrer" aria-label="LinkedIn" className="flex h-9 w-9 items-center justify-center rounded-full bg-ink-foreground/10 text-ink-foreground transition-colors hover:bg-signal hover:text-signal-foreground">
            <Linkedin className="h-4 w-4"/>
          </a>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {vendors.map((v) => (<a key={v.name} href={v.href} target="_blank" rel="noreferrer" aria-label={v.name} className="flex h-9 w-9 items-center justify-center rounded-full bg-ink-foreground/10 transition-colors hover:bg-signal">
              <img src={v.src} alt={v.name} className={`${v.size} object-contain`} loading="lazy"/>
            </a>))}
        </div>
      </div>

      <div className="border-t border-ink-foreground/10">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-5 py-4 text-xs opacity-60 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} Violerts. All rights reserved.</p>
          <p>Built for owners, managers, expediters and architects.</p>
        </div>
        <div className="mx-auto flex max-w-6xl flex-wrap gap-x-5 gap-y-1 px-5 pb-4 text-xs opacity-60">
          {legalLinks.map((l) => (<Link key={l.to} to={l.to} className="hover:opacity-100 hover:underline">
              {l.label}
            </Link>))}
        </div>
      </div>
    </footer>);
}
